import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material'
import {
  Payments as PaymentsIcon,
  People as PeopleIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  History as HistoryIcon
} from '@mui/icons-material'
import { payrollApi } from '../../../api/payrollApi'
import { usePagination, useLoading } from '../../../hooks'
import TableSkeleton from '../../../components/common/Skeleton/TableSkeleton'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

import GeneratePayrollDialog from './GeneratePayrollDialog'

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0)
}

const PayrollDashboard = () => {
  const { loading, withLoading } = useLoading()
  const pagination = usePagination(10)

  const [payrolls, setPayrolls] = useState([])
  const [totalCount, setTotalCount] = useState(0)

  const [summary, setSummary] = useState({
    total_employees: 0,
    total_base_salary: 0,
    total_lop_deduction: 0,
    total_net_salary: 0
  })

  // Defaults to current month and year
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  // Dialogs
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false)
  const [payslipDialogOpen, setPayslipDialogOpen] = useState(false)
  const [selectedPayrollId, setSelectedPayrollId] = useState(null)

  const currentYear = new Date().getFullYear()
  const years = Array.from(new Array(5), (val, index) => currentYear - index)

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' },
  ]

  const fetchData = () => {
    withLoading(async () => {
      try {
        const params = {
          month: selectedMonth,
          year: selectedYear,
          page: pagination.page + 1,
          per_page: pagination.rowsPerPage
        }

        // Fetch List
        const listResponse = await payrollApi.getPayrollList(params)
        setPayrolls(listResponse.data?.records || [])
        setTotalCount(listResponse.data?.total || 0)

        // Fetch Summary
        const summaryResponse = await payrollApi.getPayrollSummary({
          month: selectedMonth,
          year: selectedYear
        })

        if (summaryResponse.data) {
          setSummary({
            total_employees: summaryResponse.data.total_employees || 0,
            total_base_salary: summaryResponse.data.total_base_salary || 0,
            total_lop_deduction: summaryResponse.data.total_lop_deduction || 0,
            total_net_salary: summaryResponse.data.total_net_salary || 0
          })
        }
      } catch (error) {
        if (error.response?.status === 404) {
          setPayrolls([])
          setTotalCount(0)
          setSummary({
            total_employees: 0,
            total_base_salary: 0,
            total_lop_deduction: 0,
            total_net_salary: 0
          })
        } else {
          console.error('Failed to fetch payroll data:', error)
          toast.error('Failed to load payroll records')
        }
      }
    })
  }

  const navigate = useNavigate()

  useEffect(() => {
    fetchData()
  }, [pagination.page, pagination.rowsPerPage, selectedMonth, selectedYear])

  const handleViewPayslip = (payrollId) => {
    navigate(`/org/payroll/payslip/${payrollId}`)
  }

  const handleViewHistory = (employeeId) => {
    navigate(`/org/payroll/history/${employeeId}`)
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Header & Filters */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Payroll Management
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {months.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => setGenerateDialogOpen(true)}
            >
              Generate Payroll
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#1976d2', mr: 2 }}>
                    <PeopleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {summary.total_employees}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Processed Employees
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#2e7d32', mr: 2 }}>
                    <WalletIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(summary.total_base_salary)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Base Salary
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#d32f2f', mr: 2 }}>
                    <TrendingDownIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(summary.total_lop_deduction)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Deductions
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ 
              height: '100%', borderRadius: 2, border: '1px solid', borderColor: 'primary.main', 
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.08)' : '#f0f7ff' 
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <PaymentsIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="primary.main">
                      {formatCurrency(summary.total_net_salary)}
                    </Typography>
                    <Typography variant="body2" color="primary.main">
                      Total Net Payout
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Table */}
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#fafafa' }}>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Designation</TableCell>
                  <TableCell>Base Salary</TableCell>
                  <TableCell>Deductions</TableCell>
                  <TableCell>Net Salary</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton columns={7} rows={pagination.rowsPerPage} />
                ) : payrolls.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="textSecondary">
                        No payroll records found for the selected month.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payrolls.map((record) => (
                    <TableRow key={record.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2' }}>
                            {record.employee_name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {record.employee_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {record.employee_code}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{record.department || '-'}</TableCell>
                      <TableCell>{record.designation || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatCurrency(record.base_salary)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {record.lop_deduction > 0 ? (
                            <Typography component="span" color="error.main" fontWeight="medium">
                              {formatCurrency(record.lop_deduction)}
                            </Typography>
                          ) : '₹0'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                          {formatCurrency(record.net_salary)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          color="info" 
                          onClick={() => handleViewHistory(record.employee_id)}
                          title="View History"
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={() => handleViewPayslip(record.id)}
                          title="View Payslip"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 20]}
            component="div"
            count={totalCount}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          />
        </Paper>
      </Paper>

      {/* Dialogs */}
      <GeneratePayrollDialog 
        open={generateDialogOpen} 
        onClose={() => setGenerateDialogOpen(false)} 
        onSuccess={fetchData}
      />
    </Box>
  )
}

export default PayrollDashboard
